import React, { useState, useEffect } from "react";
import {
  getAdminBookings,
  getAllProviders,
  updateBookingProvider,
  updateBookingConfirmation,
} from "./adminService";

import { ProviderSelect } from "./SubComponent/ProviderSelect";
import "./ManagerScreen.css";



const ManagerScreen = () => {
  const token = localStorage.getItem("@uphealth_login_token");

  const [allProviders, setAllProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [unresponsiveBookings, setUnresponsiveBookings] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 1,
  });


  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const fetchData = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);
    try {
      if (allProviders.length === 0) {
        const providersRes = await getAllProviders(token);
        setAllProviders(providersRes.data);
      }
      const bookingsRes = await getAdminBookings(
        token,
        page,
        paginationInfo.pageSize,
        search 
      );

      if (bookingsRes.data && bookingsRes.data.pagination) {
        setAllBookings(bookingsRes.data.data);
        setPaginationInfo(bookingsRes.data.pagination);
      } else if (Array.isArray(bookingsRes.data)) {
        console.warn(
          "API response is not paginated. Please update your server."
        );
        setAllBookings(bookingsRes.data);
        setPaginationInfo({
          currentPage: 1,
          pageSize: 15,
          totalItems: bookingsRes.data.length,
          totalPages: 1,
        });
      } else {
        throw new Error("Received an unexpected data format from the API.");
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData(1, debouncedSearchTerm);
  }, [token, debouncedSearchTerm]);

  useEffect(() => {
    if (Array.isArray(allBookings)) {
      const filtered = allBookings.filter((booking) => booking.paid === false);
      setUnresponsiveBookings(filtered);
    } else {
      setUnresponsiveBookings([]);
    }
  }, [allBookings]);

  const handleProviderChange = async (booking_id, new_provider_id) => {
    if (!new_provider_id) return;
    try {
      await updateBookingProvider(token, booking_id, new_provider_id);
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert("Failed to update provider.");
      console.error(err);
    }
  };


  const handleConfirmationSubmit = async (booking_id, acceptanceStatus) => {
    try {

      await updateBookingConfirmation(token, booking_id, acceptanceStatus); //
      fetchData(paginationInfo.currentPage);
    } catch (err) {
      alert(
        `Failed to ${acceptanceStatus ? "confirm" : "unconfirm"} booking. ${
          err.response?.data || err.message
        }`
      );
      console.error(err);
    }
  };

  const getBookingStatus = (booking) => {
    if (booking.paid) {
      return { text: "Paid", class: "bg-primary text-dark" };
    }

    if (booking.acceptance === true) {
      return { text: "Confirmed", class: "bg-primary" };
    }
    if (booking.acceptance === false) {
      return { text: "Declined", class: "bg-secondary" };
    }

    return { text: "Pending", class: "bg-warning text-dark" };
  };

  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      fetchData(paginationInfo.currentPage + 1, debouncedSearchTerm);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      fetchData(paginationInfo.currentPage - 1, debouncedSearchTerm);
    }
  };



  if (loading && allProviders.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading Dashboard...</span>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="manager-container">

      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(255, 255, 255, 0.8)", zIndex: 1050 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Updating...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card manager-topbar">
        <div className="manager-topbar-info">
          <p>
            Showing <strong className="text-dark">{unresponsiveBookings.length}</strong> unresponsive bookings on this page. Total items: <strong className="text-dark">{paginationInfo.totalItems}</strong>
          </p>
        </div>
        <div className="manager-search">
          <i className="bi bi-search"></i>
          <input
            type="text"
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>


      <div className="booking-grid">
        {unresponsiveBookings.length === 0 && !loading && (
          <div className="text-center py-5" style={{ gridColumn: "1 / -1" }}>
            <i className="bi bi-check2-circle text-muted" style={{ fontSize: "4rem" }}></i>
            <h4 className="mt-3 text-dark fw-bold">No unresponsive bookings</h4>
            <p className="text-muted">You're all caught up! Try adjusting your search term if needed.</p>
          </div>
        )}
        {unresponsiveBookings.map((booking) => {
          const status = getBookingStatus(booking);

          const nearbyProviders = allProviders.filter(
            (provider) =>
              provider.city.toLowerCase() === booking.city.toLowerCase() &&
              provider.country.toLowerCase() === booking.country.toLowerCase()
          );

          return (
            <div key={booking.booking_id} className="card booking-card">
                <div className="booking-card-header">
                  <div>
                    <h5 className="fw-bold mb-1">
                      {booking.first_name} {booking.last_name}
                    </h5>
                    <div className="text-muted small">
                      ID: <span className="font-monospace fw-bold">{booking.booking_id}</span>
                    </div>
                  </div>
                  <span className={`badge ${status.class}`}>{status.text}</span>
                </div>
                
                <div className="booking-card-body">
                  <div className="booking-info-row">
                    <span className="booking-info-label"><i className="bi bi-heart-pulse"></i> Service</span>
                    <span className="booking-info-value">{booking.care_service_name}</span>
                  </div>
                  <div className="booking-info-row">
                    <span className="booking-info-label"><i className="bi bi-geo-alt"></i> Location</span>
                    <span className="booking-info-value">{booking.city}, {booking.country}</span>
                  </div>
                  <div className="booking-info-row">
                    <span className="booking-info-label"><i className="bi bi-person-badge"></i> Provider</span>
                    <span className="booking-info-value">{booking.provider_name || "Unassigned"}</span>
                  </div>
                </div>

                <div className="booking-card-footer">
                  <div className="mb-3">
                    <label className="form-label small text-muted fw-semibold mb-2">Change Provider (Nearby)</label>
                    <div className="provider-select-wrapper">
                      {nearbyProviders.length === 0 ? (
                        <div className="d-flex align-items-center p-2 border rounded bg-transparent small text-muted">
                          <i className="bi bi-info-circle me-2"></i> No nearby providers available.
                        </div>
                      ) : (
                        <ProviderSelect
                          providers={nearbyProviders}
                          selectedProviderId={booking.provider_id}
                          onChange={(new_provider_id) =>
                            handleProviderChange(booking.booking_id, new_provider_id)
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-dark btn-sm px-3 d-flex align-items-center gap-2"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span>Actions</span>
                        <i className="bi bi-three-dots"></i>
                      </button>
                      <ul className="dropdown-menu action-menu action-menu-dark shadow-sm">
                        <li className="dropend">
                          <div className="dropdown-item d-flex justify-content-between align-items-center" style={{ cursor: "pointer" }}>
                            <span className="fw-semibold"><i className="bi bi-arrow-left-right me-2"></i>Update Status</span>
                            <i className="bi bi-chevron-right small"></i>
                          </div>
                          <ul className="dropdown-menu action-menu action-menu-dark shadow-sm">
                            <li>
                              <button className="dropdown-item fw-semibold" onClick={() => handleConfirmationSubmit(booking.booking_id, true)}>
                                <i className="bi bi-check2-circle me-2 text-custom" style={{ color: "var(--color-primary)" }}></i> Confirm
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item fw-semibold" onClick={() => handleConfirmationSubmit(booking.booking_id, false)}>
                                <i className="bi bi-x-circle me-2 text-custom" style={{ color: "#ff6b6b" }}></i> Unconfirm
                              </button>
                            </li>
                          </ul>
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                          <button className="dropdown-item" onClick={() => alert("Contact Patient feature is coming soon!")}>
                            <i className="bi bi-envelope me-2"></i> Contact Patient
                          </button>
                        </li>
                        <li>
                          <button className="dropdown-item" onClick={() => alert("Edit Booking feature is coming soon!")}>
                            <i className="bi bi-pencil-square me-2"></i> Edit Booking
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      <nav className="manager-pagination">
        <button
          className="btn btn-outline-secondary"
          onClick={handlePreviousPage}
          disabled={paginationInfo.currentPage <= 1 || loading}
        >
          &larr; Previous
        </button>
        <span className="text-muted">
          Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
        </span>
        <button
          className="btn btn-outline-secondary"
          onClick={handleNextPage}
          disabled={
            paginationInfo.currentPage >= paginationInfo.totalPages || loading
          }
        >
          Next &rarr;
        </button>
      </nav>
    </div>
  );
};

export default ManagerScreen;
